import {
  ApplicationView,
  StartControllerScope,
  useController,
} from '@aha-app/mvc';
import * as Sentry from '@sentry/browser';
import Tippy, { useSingleton } from '@tippyjs/react';
import classNames from 'classnames';
import React, { useEffect, useMemo, useRef } from 'react';
import CursorLayer from './collaboration/CursorLayer';
import { smartRecordItem } from './components/aha_records/SmartRecordsPanel';
import EmptyState from './components/EmptyState';
import { facilitationSidePanelItems } from './components/facilitation';
import { historySidePanelItem } from './components/history/HistoryPanel';
import { smartZoneItem } from './components/smart_zone/SmartZonePanel';
import { smartObjectSidePanelItem } from './components/SmartObjectEditor/smartObjectSidePanelItem';
import { useMouseArea } from './components/hooks/useMouseArea';
import WhiteboardMessageComponent from './components/messages/WhiteboardMessageComponent';
import InteractionExitButton from './components/InteractionExitButton';
import RecordLinkModal from './components/modals/RecordLinkModal';
import PointerInputHelpModal from './components/modals/PointerInputHelpModal';
import SearchBar from './components/search-bar/SearchBar';
import UrlInputModal from './components/url-input/UrlInputModal';
import SidePanel from './components/SidePanel/SidePanel';
import { TippyTargetProvider } from './components/tippyContext';
import ConnectorPointMenu from './ConnectorPointMenu';
import ConnectorShapeMenu from './ConnectorShapeMenu';
import { TOOLTIP_DELAY_GENERAL } from './constants';
import ContextMenu from './ContextMenu';
import HoverController from './controllers/HoverController';
import IntegrationsController, {
  type IntegrationsControllerProps,
} from './controllers/IntegrationsController';
import PresentationController, {
  type PresentationControllerProps,
} from './controllers/PresentationController';
import RecordLinkController from './controllers/RecordLinkController';
import SmartRecordsController from './controllers/SmartRecordsController';
import WhiteboardController, {
  type WhiteboardControllerProps,
} from './controllers/WhiteboardController';
import WidgetsController from './controllers/WidgetsController';
import editorStyles from './editorStyles.css';
import ErrorHandler from './ErrorHandler';
import FileCapture from './FileCapture';
import IntegrationRecordsModal from './integrations/create-records/IntegrationRecordsModal';
import ExternalAuthModal from './integrations/ExternalAuthModal';
import Canvas from './objects/Canvas';
import { canConvertSmartRecords } from './objects/guards';
import Frames from './presentations/Frames';
import Matte from './presentations/Matte';
import presentationStyles from './presentations/presentations.module.css';
import { presentationSidePanelItems } from './presentations/sidepanel';
import CreateRecordsModal from './records/CreateRecordsModal';
import { type SidePanelItem } from './shared-types';
import styles from './styles.module.css';
import ActionsToolbar from './toolbars/ActionsToolbar';
import BaselineToolbarContainer from './toolbars/BaselineToolbarContainer';
import HoveredLinkPopup from './toolbars/HoveredLinkPopup';
import ObjectToolbar from './toolbars/ObjectToolbar';
import PresentationToolbar from './toolbars/PresentationToolbar';
import StylusToolbar from './toolbars/StylusToolbar';
import UndoRedoToolbar from './toolbars/UndoRedoToolbar';
import ZoomToolbar from './toolbars/ZoomToolbar';
import { type Rect } from './types';
import addTestHelpers from './utils/testHelpers';
import version from './version';
import VersionPreview from './VersionPreview';
import AssistantToolbar from './components/Assistant/AssistantToolbar';
import { CustomShapeNameModal } from './stylus/CustomShapeNameModal';
import OverlayToolbar from './toolbars/OverlayToolbar';
import { smartObjectsEnabled } from './objects/SmartObject';
import { tippyPopoverPlugin } from './utils/tippyPopoverPlugin';
import RoadmapImportModal from './components/modals/RoadmapImportModal';

Sentry.setTag('product', 'Whiteboard');
Sentry.setTag('whiteboard_version', version);

export interface WhiteboardProps
  extends WhiteboardControllerProps,
    PresentationControllerProps,
    IntegrationsControllerProps {
  allowUpdate: boolean;
  allowAnnotate: boolean;
  commentsEnabled: boolean;
  sidePanelItems?: SidePanelItem[];

  // Whether the whiteboard should show a CTA if the whiteboard is empty
  shouldShowEmptyState: boolean;

  // Controls to render in the top-right area of the whiteboard
  renderControls?: React.ReactNode;

  isHistoryPreview?: false;
}

interface PreviewWhiteboardProps {
  isHistoryPreview: true;
  initialData: any;
  readOnly: true;
  canPresent: false;
}

const Whiteboard = (props: WhiteboardProps | PreviewWhiteboardProps) => {
  const isHistoryPreview = props.isHistoryPreview;

  const {
    allowUpdate = false,
    allowAnnotate = false,
    commentsEnabled = false,
    shouldShowEmptyState = false,
    sidePanelItems = undefined,
    renderControls = undefined,
  } = isHistoryPreview ? {} : props;

  const controller = useController(WhiteboardController);
  const widgetsController = useController(WidgetsController);
  const hoverController = useController(HoverController);
  const presentationController = useController(PresentationController);
  const integrationsController = useController(IntegrationsController);
  const recordLinkController = useController(RecordLinkController);
  const smartRecordsController = useController(SmartRecordsController);

  const { renderTemplateModal, isFormField, formFieldName, isEmbeddedPreview } =
    controller.props;
  const {
    aiAssistantVisible,
    selectedRect,
    canvasReady,
    choosingTemplate,
    createRecords,
    customShapeNameModalProps,
    isEmpty,
    message,
    error,
  } = controller.state;

  const { canFacilitate, canPresent, canUpdateContent } = controller;

  const readOnly = controller.isReadOnly();

  const canvasContainer = useRef<HTMLDivElement>(null);
  const canvasWrapper = useRef<HTMLDivElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const externalContentRef = useRef<HTMLDivElement>(null);
  const portalContentRef = useRef<HTMLDivElement>(null);
  const fabricRef = useRef<Canvas>();
  const editorContainerRef = useRef<HTMLDivElement>(null);
  const canvasxInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (
      !canvasContainer.current ||
      !canvasRef.current ||
      !externalContentRef.current ||
      !portalContentRef.current
    ) {
      return;
    }
    if (fabricRef.current) return;

    // Call startController only once we are ready to also create the canvas. Note, we can't call it after
    // creating the canvas, because doing so kicks off some activity that requires the controller to be started
    controller.startController(presentationController, integrationsController);
    // since this depends on the main controller initialisation, it needs to come after
    widgetsController.startController();

    controller.storeControllerInDOM(canvasContainer);

    fabricRef.current = new Canvas(
      controller,
      canvasContainer.current,
      canvasRef.current,
      externalContentRef.current,
      portalContentRef.current,
      {}
    );
    controller.actionSetCanvas(
      fabricRef.current,
      editorContainerRef.current as HTMLDivElement,
      canvasxInputRef.current as HTMLInputElement,
      canvasContainer.current
    );
    hoverController.actionSetCanvas(fabricRef.current);

    presentationController.startController(fabricRef.current, controller);
    integrationsController.startController(fabricRef.current, controller);
    recordLinkController.startController(fabricRef.current, controller);
    smartRecordsController.startController(fabricRef.current, controller);

    addTestHelpers(controller);

    return function cleanup() {
      // The canvas will be cleaned up by the controller.
    };
  }, [
    controller,
    hoverController,
    presentationController,
    widgetsController,
    integrationsController,
    recordLinkController,
    smartRecordsController,
  ]);

  const [mouseNearBottom, mouseAreaHandler] = useMouseArea(160);

  if (process.env.TEST) {
    // @ts-expect-error For testing
    window.controller = controller;
  }

  const smartZonesEnabled = controller.smartZonesEnabled();

  const visibleSidePanelItems = useMemo(() => {
    const hasWhiteboardsAdvanced = window.currentAccount?.whiteboardsAdvanced;
    const showPromotions =
      window.currentAccount?.hasCrossProductPromotionsEnabled;

    const items = [
      ...(sidePanelItems ?? []),
      ...(hasWhiteboardsAdvanced || showPromotions
        ? facilitationSidePanelItems
        : []),
      ...presentationSidePanelItems,
    ];

    if (canUpdateContent && (canConvertSmartRecords() || showPromotions)) {
      items.push(smartRecordItem(smartRecordsController));
    }

    if (canUpdateContent && smartZonesEnabled) {
      items.push(smartZoneItem);
    }

    if (smartObjectsEnabled()) {
      items.push(smartObjectSidePanelItem);
    }

    if (controller.props.allowHistory && controller.historyManager) {
      items.push(historySidePanelItem(controller.historyManager));
    }

    return items.filter(item => {
      return !item.hidden?.({ canFacilitate, canPresent });
    });
  }, [
    sidePanelItems,
    controller.props.allowHistory,
    smartRecordsController,
    controller.historyManager,
    canFacilitate,
    canPresent,
    canUpdateContent,
    smartZonesEnabled,
  ]);

  const matte = presentationController.isPresenting()
    ? presentationController.matteDimensions()
    : undefined;
  const sidepanelEnabled =
    visibleSidePanelItems.length > 0 &&
    !controller.props.sidepanelDisabled &&
    !isEmbeddedPreview;
  const sidepanelHidden = presentationController.isPresenting();

  const { authPromptStatus } = integrationsController.state;
  const allWidgets = widgetsController.getAllWidgets();

  const createIntegrationRecordsProps =
    integrationsController.getRecordCreationProps();

  // a special tippy instance for when we want the tooltip rendered above the target
  const [topTippySource, topTippyTarget] = useSingleton();
  const [defaultTippySource, defaultTippyTarget] = useSingleton();

  const { showRecordLinkModal } = recordLinkController;

  return (
    <ErrorHandler controller={controller} error={error}>
      <Tippy
        singleton={topTippySource}
        placement='top'
        delay={TOOLTIP_DELAY_GENERAL}
        plugins={[tippyPopoverPlugin]}
      />
      <Tippy
        singleton={defaultTippySource}
        delay={TOOLTIP_DELAY_GENERAL}
        plugins={[tippyPopoverPlugin]}
      />
      <TippyTargetProvider value={defaultTippyTarget}>
        <div
          className={classNames(styles.whiteboardContainer, {
            [presentationStyles.framesVisible]:
              presentationController.framesVisible(),
          })}
        >
          <div
            className={styles.whiteboard}
            ref={canvasContainer}
            tabIndex={0}
            onMouseMove={event => {
              mouseAreaHandler(event);
            }}
          >
            <div ref={externalContentRef} />
            <div
              className={styles.canvasWrapper}
              data-testid='canvas-wrapper'
              onMouseMove={event => {
                controller.trackCursor(event);
              }}
              ref={canvasWrapper}
            >
              <canvas ref={canvasRef} />
              <div
                ref={editorContainerRef}
                className={editorStyles.editorContainer}
              />
              <div className='canvasx-text-input' ref={canvasxInputRef} />
              <EmptyState
                shouldShowEmptyState={shouldShowEmptyState}
                isEmpty={isEmpty}
              />
            </div>
            {canvasReady && (
              <div onMouseUp={() => controller.actionCanvasMouseUp()}>
                {!isEmbeddedPreview && (
                  <>
                    {matte ? <Matte {...matte} /> : null}

                    {presentationController.isPresenting() ? (
                      <PresentationToolbar visible={mouseNearBottom} />
                    ) : null}

                    <div
                      className={classNames(styles.previewAndToolbars, {
                        [styles.sidePanelEnabled]:
                          !!visibleSidePanelItems.length,
                      })}
                    >
                      <div className={styles.viewableCanvasArea}>
                        <BaselineToolbarContainer
                          aiAssistantVisible={aiAssistantVisible}
                        >
                          {!presentationController.isPresenting() && (
                            <>
                              {(allowUpdate || allowAnnotate) &&
                                !readOnly &&
                                aiAssistantVisible && <AssistantToolbar />}
                              <TippyTargetProvider value={topTippyTarget}>
                                <ActionsToolbar
                                  commentsEnabled={
                                    commentsEnabled && allowAnnotate
                                  }
                                />
                                {!readOnly && <UndoRedoToolbar />}
                                <ZoomToolbar />
                                <OverlayToolbar />
                              </TippyTargetProvider>
                            </>
                          )}
                        </BaselineToolbarContainer>
                        {!isHistoryPreview && <VersionPreview />}
                      </div>
                      {renderControls && (
                        <div className={styles.whiteboardControls}>
                          {renderControls}
                        </div>
                      )}
                    </div>
                    {allWidgets && (
                      <div className='flex-row flex-row--justify-center'>
                        <div className={styles.widgetsContainer}>
                          {allWidgets.map(widget => widget.render())}
                        </div>
                      </div>
                    )}
                  </>
                )}
                {!readOnly && (
                  <>
                    {(allowUpdate || allowAnnotate) && !isEmbeddedPreview && (
                      <CursorLayer />
                    )}
                    {(allowUpdate || allowAnnotate) &&
                      controller.isObjectToolbarVisible() && (
                        <ObjectToolbar rect={selectedRect as Rect} />
                      )}
                    {allowUpdate && (
                      <>
                        {!controller.isCropping() && <StylusToolbar />}
                        <ContextMenu />
                        <ConnectorPointMenu />
                        <ConnectorShapeMenu />
                        {controller.state.externalContentInteractive && (
                          <InteractionExitButton
                            onClick={() =>
                              controller.canvas?.externalContent?.blur()
                            }
                          />
                        )}
                        {choosingTemplate && renderTemplateModal?.(controller)}
                        {createRecords && (
                          <CreateRecordsModal
                            data={createRecords.data}
                            recordType={createRecords.recordType}
                          />
                        )}
                        {controller.state.showAddAhaViewModal &&
                          controller.props.renderAddAhaViewModal?.(controller)}
                        {controller.state.infoForRoadmapImportModal && (
                          <RoadmapImportModal
                            whiteboardController={controller}
                            bookmarkInfo={
                              controller.state.infoForRoadmapImportModal
                            }
                          ></RoadmapImportModal>
                        )}
                        {customShapeNameModalProps && (
                          <CustomShapeNameModal
                            {...customShapeNameModalProps}
                          />
                        )}
                        {createIntegrationRecordsProps && (
                          <IntegrationRecordsModal
                            {...createIntegrationRecordsProps}
                          />
                        )}
                        {authPromptStatus?.status === 'active' &&
                          (authPromptStatus.promptType === 'importModal' ||
                            authPromptStatus.promptType === 'exportModal') && (
                            <ExternalAuthModal
                              promptType={authPromptStatus.promptType}
                              mode={authPromptStatus.mode}
                              service={authPromptStatus.service}
                              onClose={() => authPromptStatus.action(false)}
                              onConfirm={() => authPromptStatus.action(true)}
                            />
                          )}
                        {showRecordLinkModal && <RecordLinkModal />}
                        {controller.state.showPointerInputHelpModal && (
                          <PointerInputHelpModal
                            onClose={() =>
                              controller.actionHidePointerInputHelp()
                            }
                          />
                        )}
                        <FileCapture canvasContainer={canvasWrapper} />
                      </>
                    )}
                  </>
                )}
                <WhiteboardMessageComponent
                  message={message || authPromptStatus}
                />
                {isFormField && (
                  <input
                    id='whiteboardContent'
                    name={formFieldName}
                    type='hidden'
                    value={JSON.stringify(controller.whiteboardContentAsJSON)}
                  />
                )}
              </div>
            )}
          </div>

          <SearchBar
            whiteboardController={controller}
            visible={controller.searchBarVisible()}
          />
          {controller.state.urlInputModalObject && (
            <UrlInputModal
              sourceObject={controller.state.urlInputModalObject}
              whiteboardController={controller}
            />
          )}
          {presentationController.isPresenting() ? (
            <Frames
              frames={presentationController.getFrames()}
              currentFrameIndex={presentationController.currentFrameIndex()}
              navigate={frameId => {
                presentationController.actionNavigateToFrame(frameId);
                controller.actionFocus();
              }}
              close={() => presentationController.actionToggleFrames(false)}
            />
          ) : null}
          {sidepanelEnabled && canvasReady && !isHistoryPreview && (
            <SidePanel
              className={styles.sidePanelOverrides}
              hidden={sidepanelHidden}
              controllerRef={ref => (controller.sidePanelController = ref)}
              documentType='Whiteboard'
              sidePanelItems={visibleSidePanelItems}
              onChange={controller.actionSidePanelStateChange}
            />
          )}
        </div>
        {canvasReady && <HoveredLinkPopup canvas={controller.canvas} />}
        <div ref={portalContentRef} />
      </TippyTargetProvider>
    </ErrorHandler>
  );
};

function addControllersToWhiteboard() {
  return StartControllerScope(
    IntegrationsController,
    StartControllerScope(
      PresentationController,
      StartControllerScope(
        HoverController,
        StartControllerScope(
          WhiteboardController,
          StartControllerScope(
            WidgetsController,
            StartControllerScope(
              RecordLinkController,
              StartControllerScope(
                SmartRecordsController,
                ApplicationView(Whiteboard)
              )
            )
          )
        )
      )
    )
  );
}

const PreviewWhiteboard = addControllersToWhiteboard() as typeof Whiteboard;

export { PreviewWhiteboard };

export default addControllersToWhiteboard();
